import { useState, useRef } from 'react'
import { Box, Flex, Text, VStack, T, useT, toaster } from '@minetrace/ui'
import { LuDownload, LuUpload, LuFileCheck, LuTrash2, LuShieldCheck, LuTriangleAlert } from 'react-icons/lu'
import { useFormColors } from '@minetrace/ui/forms'
import { proveedoresService } from '../../../api/services'
import type { FileAttachmentResponse } from '../../../api/models/response/proveedor'

interface Step0ConsentProps {
  consentFile: File | null
  onFileChange: (file: File | null) => void
  companyId: string | null
  ruc: string
  error?: { code: string; message: string } | null
  existingConsent?: FileAttachmentResponse | null
  existingConsentAcceptedAt?: string | null
}

export default function Step0Consent({ consentFile, onFileChange, companyId, ruc, error, existingConsent, existingConsentAcceptedAt }: Step0ConsentProps) {
  const c = useFormColors()
  const { t } = useT()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)
  const [downloading, setDownloading] = useState(false)

  const handleDownloadTemplate = async () => {
    if (!companyId) {
      toaster.error({ description: 'Complete primero los pasos previos del registro.' })
      return
    }
    setDownloading(true)
    try {
      await proveedoresService.downloadConsentTemplate(companyId, ruc || 'minero')
    } catch (e) {
      toaster.error({ description: e instanceof Error ? e.message : 'No se pudo descargar la plantilla' })
    } finally {
      setDownloading(false)
    }
  }

  const handleFileSelect = (file: File) => {
    if (file.size === 0) {
      toaster.error({ description: 'El archivo está vacío (0 KB). Seleccione un archivo con contenido.' })
      return
    }
    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
    if (!isPdf) {
      toaster.error({ description: 'El documento de consentimiento debe ser un archivo PDF.' })
      return
    }
    onFileChange(file)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer.files[0]
    if (file) handleFileSelect(file)
  }

  return (
    <VStack gap={6} alignItems="stretch" flex={1}>
      {/* Intro */}
      <Flex gap={3} alignItems="center">
        <Box p={2.5} borderRadius="lg" bg="rgba(1, 45, 29, 0.06)">
          <LuShieldCheck size={22} color="#b4532a" />
        </Box>
        <Box>
          <T tid="proveedores.wizard.step0.intro" fontSize="sm" color={c.textSecondary} lineHeight="tall" />
        </Box>
      </Flex>

      {error && (
        <Flex
          gap={3}
          alignItems="flex-start"
          p={4}
          borderRadius="lg"
          border="1px solid"
          borderColor="rgba(200, 68, 47, 0.35)"
          bg="rgba(200, 68, 47, 0.08)"
        >
          <Box flexShrink={0} pt={0.5}><LuTriangleAlert size={20} color="#9a3020" /></Box>
          <Box flex={1}>
            <Text fontSize="sm" fontWeight="700" color="#9a3020">
              {error.code === 'CONSENT_TEMPLATE_VERSION_CHANGED'
                ? 'La plantilla de consentimiento cambió'
                : 'El documento no coincide con la plantilla emitida'}
            </Text>
            <Text fontSize="sm" color="#9a3020" mt={1}>{error.message}</Text>
            <Box
              as="button"
              type="button"
              mt={3}
              px={3}
              py={2}
              borderRadius="md"
              bg="#9a3020"
              color="white"
              fontSize="xs"
              fontWeight="700"
              cursor={downloading ? 'not-allowed' : 'pointer'}
              opacity={downloading ? 0.6 : 1}
              _hover={downloading ? {} : { opacity: 0.9 }}
              onClick={downloading ? undefined : handleDownloadTemplate}
            >
              {downloading ? 'Descargando...' : 'Volver a descargar la plantilla'}
            </Box>
          </Box>
        </Flex>
      )}

      {/* Two columns */}
      <Flex gap={6} flexWrap={{ base: 'wrap', md: 'nowrap' }}>
        {/* Left: Download template */}
        <Box flex={1} minW="280px" p={6} borderRadius="xl" border="1px solid" borderColor={c.border} bg={c.bgCard}>
          <VStack gap={4} alignItems="stretch">
            <Flex gap={3} alignItems="center">
              <Box
                w="40px" h="40px" borderRadius="lg"
                bg="rgba(1, 45, 29, 0.08)"
                display="flex" alignItems="center" justifyContent="center"
                flexShrink={0}
              >
                <LuDownload size={18} color="#b4532a" />
              </Box>
              <Box>
                <T tid="proveedores.wizard.step0.downloadTitle" fontSize="md" fontWeight="700" color={c.text} />
                <T tid="proveedores.wizard.step0.downloadSubtitle" fontSize="xs" color={c.textSecondary} />
              </Box>
            </Flex>

            <T tid="proveedores.wizard.step0.downloadDescription" fontSize="sm" color={c.textSecondary} lineHeight="tall" />

            <Box
              as="button"
              type="button"
              display="flex"
              alignItems="center"
              justifyContent="center"
              gap={2}
              w="100%"
              py={3}
              borderRadius="lg"
              bg="#b4532a"
              color="white"
              fontWeight="700"
              fontSize="sm"
              cursor={downloading ? 'not-allowed' : 'pointer'}
              opacity={downloading ? 0.6 : 1}
              _hover={downloading ? {} : { opacity: 0.9 }}
              onClick={downloading ? undefined : handleDownloadTemplate}
            >
              <LuDownload size={16} />
              {downloading ? 'Generando...' : t('proveedores.wizard.step0.downloadButton')}
            </Box>
          </VStack>
        </Box>

        {/* Right: Upload signed document */}
        <Box flex={1} minW="280px" p={6} borderRadius="xl" border="1px solid" borderColor={c.border} bg={c.bgCard}>
          <VStack gap={4} alignItems="stretch">
            <Flex gap={3} alignItems="center">
              <Box
                w="40px" h="40px" borderRadius="lg"
                bg="rgba(1, 45, 29, 0.08)"
                display="flex" alignItems="center" justifyContent="center"
                flexShrink={0}
              >
                <LuUpload size={18} color="#b4532a" />
              </Box>
              <Box>
                <T tid="proveedores.wizard.step0.uploadTitle" fontSize="md" fontWeight="700" color={c.text} />
                <T tid="proveedores.wizard.step0.uploadSubtitle" fontSize="xs" color={c.textSecondary} />
              </Box>
            </Flex>

            {!consentFile && existingConsent ? (
              <>
                <Flex
                  p={4}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor="#2a8a87"
                  bg="rgba(42, 138, 135, 0.06)"
                  alignItems="center"
                  gap={3}
                >
                  <Box
                    w="40px" h="40px" borderRadius="lg"
                    bg="rgba(42, 138, 135, 0.15)"
                    display="flex" alignItems="center" justifyContent="center"
                    flexShrink={0}
                  >
                    <LuFileCheck size={20} color="#1d6664" />
                  </Box>
                  <Box flex={1} overflow="hidden">
                    <Text fontSize="sm" fontWeight="600" color={c.text} truncate>
                      {existingConsent.originalFileName ?? 'Consentimiento.pdf'}
                    </Text>
                    {existingConsentAcceptedAt && (
                      <Text fontSize="xs" color={c.textSecondary}>
                        Subido el {new Date(existingConsentAcceptedAt).toLocaleString('es-PE')}
                      </Text>
                    )}
                  </Box>
                </Flex>
                <Text fontSize="xs" color="#1d6664" fontWeight="600">
                  Documento previamente firmado y aceptado.
                </Text>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) handleFileSelect(file)
                    e.target.value = ''
                  }}
                />
                <Box
                  as="button"
                  type="button"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  gap={2}
                  w="100%"
                  py={2.5}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor={c.border}
                  bg="transparent"
                  color={c.text}
                  fontWeight="600"
                  fontSize="sm"
                  cursor="pointer"
                  _hover={{ borderColor: '#b4532a', bg: 'rgba(1, 45, 29, 0.02)' }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <LuUpload size={14} />
                  Reemplazar documento
                </Box>
              </>
            ) : !consentFile ? (
              <>
                <T tid="proveedores.wizard.step0.uploadDescription" fontSize="sm" color={c.textSecondary} lineHeight="tall" />

                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) handleFileSelect(file)
                    e.target.value = ''
                  }}
                />

                <Box
                  p={6}
                  borderRadius="lg"
                  border="2px dashed"
                  borderColor={dragOver ? '#b4532a' : c.border}
                  bg={dragOver ? 'rgba(1, 45, 29, 0.04)' : 'transparent'}
                  textAlign="center"
                  cursor="pointer"
                  transition="all 0.15s"
                  _hover={{ borderColor: '#b4532a', bg: 'rgba(1, 45, 29, 0.02)' }}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e: React.DragEvent) => { e.preventDefault(); setDragOver(true) }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={handleDrop}
                >
                  <VStack gap={2}>
                    <LuUpload size={24} color="#857b71" />
                    <T tid="proveedores.wizard.step0.dropzoneMain" fontSize="sm" fontWeight="600" color={c.text} />
                    <Text fontSize="xs" color={c.textMuted}>Solo archivos PDF (la plantilla descargada, firmada)</Text>
                  </VStack>
                </Box>
              </>
            ) : (
              <>
                <Flex
                  p={4}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor="#2a8a87"
                  bg="rgba(42, 138, 135, 0.06)"
                  alignItems="center"
                  gap={3}
                >
                  <Box
                    w="40px" h="40px" borderRadius="lg"
                    bg="rgba(42, 138, 135, 0.15)"
                    display="flex" alignItems="center" justifyContent="center"
                    flexShrink={0}
                  >
                    <LuFileCheck size={20} color="#1d6664" />
                  </Box>
                  <Box flex={1} overflow="hidden">
                    <Text fontSize="sm" fontWeight="600" color={c.text} truncate>
                      {consentFile.name}
                    </Text>
                    <Text tid="proveedores.wizard.step0.fileSize" values={{ size: (consentFile.size / 1024).toFixed(0) }} fontSize="xs" color={c.textSecondary} />
                  </Box>
                  <Box
                    as="button"
                    type="button"
                    p={2}
                    borderRadius="md"
                    cursor="pointer"
                    color="#c8442f"
                    _hover={{ bg: 'rgba(200, 68, 47, 0.1)' }}
                    onClick={() => onFileChange(null)}
                  >
                    <LuTrash2 size={16} />
                  </Box>
                </Flex>
                <T tid="proveedores.wizard.step0.fileAttached" fontSize="xs" color="#1d6664" fontWeight="600" />
              </>
            )}
          </VStack>
        </Box>
      </Flex>
    </VStack>
  )
}
